import { z } from 'zod';

import type { ArcLimits } from '@/api/generated';

import type { Arc, ArcItem, ArcSection } from './arcTypes';

/*
 * The ARC's size limits, so the page stops at them before the server has to.
 * Every number is the reply's `limits`, which the server reads from its
 * settings; the page holds none of its own (arc-size-limits, D16, I4).
 */

/** A number as the server writes it in its sentences: "1,000". */
export function formatCount(count: number): string {
  return count.toLocaleString('en-US');
}

/** True when an item counts toward a limit: its resource is there and published now (B28). */
export function isCounted(item: ArcItem): boolean {
  return item.available && item.resource?.is_published === true;
}

/** The section's items that count, as the server counts them (B3, B4). */
export function publishedCount(section: ArcSection): number {
  return section.items.filter(isCounted).length;
}

/** The published items in the visible sections: what the ARC's total counts (B5). */
export function visiblePublished(sections: ArcSection[]): number {
  return sections
    .filter((section) => section.visible)
    .reduce((sum, section) => sum + publishedCount(section), 0);
}

/**
 * The most published items a section of this type may hold; no type is a
 * custom section. The server sends every standard type's limit.
 */
export function limitFor(
  standardType: string | null | undefined,
  limits: ArcLimits,
): number {
  const byType = limits.section_max_items;
  return byType[standardType ?? 'CUSTOM'] ?? byType.CUSTOM;
}

/** A section's limit, one added on this page included (design 8). */
export function sectionLimit(section: ArcSection, limits: ArcLimits): number {
  return limitFor(section.standard_type, limits);
}

/**
 * Why a new section, more content in a section, or showing a hidden section
 * cannot happen to this copy, or null when it can. The working copy is
 * counted by the server's rule: only published items count, a section's own
 * limit counts them whether it is shown or hidden, the ARC's total counts the
 * visible sections only. A section at its limit says so before the ARC's
 * total does, since removing from it is what lets that section grow.
 */
export function limitReasons({ sections, limits }: Pick<Arc, 'sections' | 'limits'>) {
  const total = visiblePublished(sections);
  const find = (sectionId: string) => sections.find((each) => each.id === sectionId);
  const arcFull = `An ARC can hold at most ${formatCount(limits.max_items)} published items in visible sections.`;

  return {
    newSection:
      sections.length >= limits.max_sections
        ? `An ARC can have at most ${formatCount(limits.max_sections)} sections. Delete one to add another.`
        : null,
    addTo: (sectionId: string): string | null => {
      const section = find(sectionId);
      if (!section) return null;
      const limit = sectionLimit(section, limits);
      if (publishedCount(section) >= limit) {
        return `The ${section.name} section can hold at most ${formatCount(limit)} published items.`;
      }
      return section.visible && total >= limits.max_items ? arcFull : null;
    },
    show: (sectionId: string): string | null => {
      const section = find(sectionId);
      if (!section || section.visible) return null;
      return total + publishedCount(section) > limits.max_items
        ? `Showing this section would take your ARC over ${formatCount(limits.max_items)} published items in visible sections. Remove items first.`
        : null;
    },
  };
}

/*
 * The ARC Builder's form rules, the same as the backend's
 * (`app/schemas/arc.py`): a section name of 1 to 80 characters, an ARC name
 * of 1 to 200, a description that may be left empty.
 */

const sectionName = z
  .string()
  .trim()
  .min(1, 'Give the section a name')
  .max(80, 'A section name can be at most 80 characters');

export const renameSectionSchema = z.object({ name: sectionName });
export type RenameSectionValues = z.infer<typeof renameSectionSchema>;

/** "custom", or the standard type being added back. */
export const newSectionSchema = z
  .object({
    kind: z.string().min(1, 'Choose what to add'),
    name: z.string().trim().max(80, 'A section name can be at most 80 characters'),
  })
  .refine((values) => values.kind !== 'custom' || values.name.length > 0, {
    path: ['name'],
    message: 'Give the section a name',
  });
export type NewSectionValues = z.infer<typeof newSectionSchema>;

/** The ARC's name and description, the description held to the reply's limit. */
export function detailsSchema(limits: ArcLimits) {
  const most = limits.description_max_length;
  return z.object({
    title: z
      .string()
      .trim()
      .min(1, 'Give the ARC a name')
      .max(200, 'The name can be at most 200 characters'),
    description: z
      .string()
      .trim()
      .max(most, `The description can be at most ${formatCount(most)} characters.`),
  });
}
export type DetailsValues = z.infer<ReturnType<typeof detailsSchema>>;

export const moveItemSchema = z.object({
  sectionId: z.string().min(1, 'Choose a section'),
});
export type MoveItemValues = z.infer<typeof moveItemSchema>;
